import { Navigate, Routes, Route, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AuthenticatedLayout } from "@/components/AuthenticatedLayout";
import { LoginPage } from "@/pages/LoginPage";
import { InviteAcceptPage } from "@/pages/InviteAcceptPage";
import { SetPasswordPage } from "@/pages/SetPasswordPage";
import { RecoverPasswordPage } from "@/pages/RecoverPasswordPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { usePermissions } from "@/hooks/usePermissions";
import { EmployeeSessionGate } from "@/components/auth/EmployeeSessionGate";
import { StudioLayout } from "@/components/studio/StudioLayout";
import { StudioEmployeeGuard } from "@/components/studio/StudioEmployeeGuard";
import { StudioIndexRedirect } from "@/components/studio/StudioIndexRedirect";
import { LazyBoundary } from "@/components/routing/LazyBoundary";
import { AdminDashboardPreviewPage } from "@/pages/preview/AdminDashboardPreviewPage";
import { AppUpdateNotice } from "@/components/AppUpdateNotice";
import {
  LazyAdminHomePage,
  LazyAssessmentIntakePage,
  LazyAssessmentSetupWizardPage,
  LazyBodyMapPage,
  LazyBundleDetailPage,
  LazyBundleListPage,
  LazyBundleWizardPage,
  LazyCapacityAssessmentPage,
  LazyCreateOrganizationPage,
  LazyCreateUserPage,
  LazySettingsPage,
  LazyCurriculumBundleHistoryPage,
  LazyEditOrganizationPage,
  LazyEmployeeDetailPage,
  LazyEmployeeListPage,
  LazyEmployeeMapsPage,
  LazyEmployeePhaseProgramPage,
  LazyEmployeePortalPage,
  LazyEmployeeSelfCarePage,
  LazyEmployeeSelfCareRegionPage,
  LazyEmployeeTestingPage,
  LazyExerciseLibraryPage,
  LazyFunctionalCapacityPage,
  LazyImportEmployeesPage,
  LazyLeadCapturePage,
  LazyLeadDetailPage,
  LazyReferenceLettersPage,
  LazyManagerDashboardPage,
  LazyMapsOverviewPage,
  LazyAssessmentPosterPage,
  LazyCapacityProspectsPage,
  LazyCapacityReportPage,
  LazyGetStartedPage,
  LazyOrganizationDetailPage,
  LazySendCapacityCheckPage,
  LazyOrganizationEmployeeEditPage,
  LazyOrgadminDashboardPage,
  LazyOrgAssessmentEntryPage,
  LazyOrgEmployeeAddRedirect,
  LazyOrgEmployeeEditPage,
  LazyOrgEmployeeHomePage,
  LazyOrgStudioPage,
  LazyPublicPlayerPage,
  LazySelfCareEditorPage,
  LazySelfCareListPage,
  LazyStudioPlayerPage,
  LazyStudioTemplatePlayerPage,
  LazyTemplateFormPage,
  LazyTemplateLibraryPage,
  LazyVideoDetailPage,
  LazyVideoLibraryPage,
} from "@/routes/lazyRouteComponents";

/** Redirects away from /login when user is already authenticated (e.g. after sign-in). */
function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}

function ForgotPasswordRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <ForgotPasswordPage />;
}

function RedirectToDefault() {
  const { defaultPath } = useAuth();
  return <Navigate to={defaultPath} replace />;
}

function OrgDashboardOrManager() {
  const { user_type, isOrgConsoleContributor, isEmployee } = usePermissions();
  if (user_type === "superadmin" || user_type === "admin") {
    return <Navigate to="/admin" replace />;
  }
  if (user_type === "orgadmin") return <LazyOrgadminDashboardPage />;
  if (user_type === "orguser" && isOrgConsoleContributor) return <LazyManagerDashboardPage />;
  if (user_type === "orguser" && isEmployee) return <LazyOrgEmployeeHomePage />;
  return <LazyOrgadminDashboardPage />;
}

/** Keyed by region so moving between regions always starts a fresh walkthrough. */
function SelfCareRegionRoute() {
  const { regionKey = "" } = useParams<{ regionKey: string }>();
  return <LazyEmployeeSelfCareRegionPage key={regionKey} />;
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        {/** Public URLs — still wrapped in AuthProvider so in-app assessment can read session */}
        <Route
          path="/play/:token"
          element={
            <LazyBoundary fullscreen>
              <LazyPublicPlayerPage />
            </LazyBoundary>
          }
        />
        <Route
          path="/capacity-check"
          element={
            <LazyBoundary fullscreen>
              <LazyCapacityAssessmentPage />
            </LazyBoundary>
          }
        />
        <Route
          path="/assessment"
          element={
            <LazyBoundary fullscreen>
              <LazyCapacityAssessmentPage />
            </LazyBoundary>
          }
        />
        <Route
          path="/assess/start"
          element={
            <LazyBoundary fullscreen>
              <LazyCapacityAssessmentPage />
            </LazyBoundary>
          }
        />
        <Route
          path="/assess/setup"
          element={
            <LazyBoundary fullscreen>
              <LazyAssessmentSetupWizardPage />
            </LazyBoundary>
          }
        />
        <Route
          path="/assess/:orgCode"
          element={
            <LazyBoundary fullscreen>
              <LazyAssessmentIntakePage />
            </LazyBoundary>
          }
        />
        <Route
          path="/assess"
          element={
            <LazyBoundary fullscreen>
              <LazyAssessmentIntakePage />
            </LazyBoundary>
          }
        />
        <Route
          path="/contact"
          element={
            <LazyBoundary fullscreen>
              <LazyLeadCapturePage />
            </LazyBoundary>
          }
        />
        <Route
          path="/get-started"
          element={
            <LazyBoundary fullscreen>
              <LazyGetStartedPage />
            </LazyBoundary>
          }
        />
        <Route
          path="/poster/:orgCode"
          element={
            <LazyBoundary fullscreen>
              <LazyAssessmentPosterPage />
            </LazyBoundary>
          }
        />
        <Route path="/preview/dashboard" element={<AdminDashboardPreviewPage />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
        <Route path="/invite-accept" element={<InviteAcceptPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/recover-password" element={<RecoverPasswordPage />} />
        <Route path="/reset-password" element={<RecoverPasswordPage />} />
        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<RedirectToDefault />} />
          <Route path="/admin" element={<LazyAdminHomePage />} />
          <Route path="/admin/settings" element={<LazySettingsPage />} />
          <Route path="/admin/users" element={<Navigate to="/admin/settings?tab=users" replace />} />
          <Route path="/admin/platform" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/leads" element={<Navigate to="/admin?tab=leads" replace />} />
          <Route path="/admin/leads/:id" element={<LazyLeadDetailPage />} />
          {/* Letter library behind the Goals & Reservations assessment. The page
              itself re-checks isHqAdmin — a route entry is not an authorisation. */}
          <Route path="/admin/reference-letters" element={<LazyReferenceLettersPage />} />
          <Route path="/admin/clients" element={<Navigate to="/admin?tab=clients" replace />} />
          <Route path="/admin/organizations" element={<Navigate to="/admin?tab=clients" replace />} />
          <Route path="/admin/organizations/new" element={<LazyCreateOrganizationPage />} />
          <Route
            path="/admin/organizations/:orgId/employees/:employeeId/edit"
            element={<LazyOrganizationEmployeeEditPage />}
          />
          <Route path="/admin/organizations/:id/edit" element={<LazyEditOrganizationPage />} />
          <Route path="/admin/organizations/:id/curriculum/history" element={<LazyCurriculumBundleHistoryPage />} />
          <Route path="/admin/capacity-checks" element={<LazyCapacityProspectsPage />} />
          <Route path="/admin/send-capacity-check" element={<LazySendCapacityCheckPage />} />
          <Route path="/admin/organizations/:id/capacity-report" element={<LazyCapacityReportPage />} />
          <Route path="/admin/organizations/:id" element={<LazyOrganizationDetailPage />} />
          <Route path="/org" element={<OrgDashboardOrManager />} />
          <Route
            path="/org/assessment/setup"
            element={<LazyAssessmentSetupWizardPage />}
          />
          <Route
            path="/org/assessment/start"
            element={<LazyCapacityAssessmentPage />}
          />
          <Route path="/org/assessment" element={<LazyOrgAssessmentEntryPage />} />
          <Route path="/org/studio" element={<LazyOrgStudioPage />} />
          <Route path="/org/employees" element={<LazyEmployeeListPage />} />
          <Route path="/org/employees/new" element={<LazyOrgEmployeeAddRedirect />} />
          <Route path="/org/employees/import" element={<LazyImportEmployeesPage />} />
          <Route path="/org/employees/:id/edit" element={<LazyOrgEmployeeEditPage />} />
          <Route path="/org/employees/:id" element={<LazyEmployeeDetailPage />} />
          <Route path="/org/users/new" element={<LazyCreateUserPage />} />
          <Route path="/body-map" element={<LazyBodyMapPage />} />
          <Route path="/my" element={<LazyEmployeePortalPage />} />
          <Route path="/my/self-care" element={<LazyEmployeeSelfCarePage />} />
          <Route path="/my/self-care/:regionKey" element={<SelfCareRegionRoute />} />
          <Route path="/my/program/phase/:phaseId" element={<LazyEmployeePhaseProgramPage />} />
          <Route path="/my/player/template/:templateId" element={<LazyStudioTemplatePlayerPage />} />
          <Route path="/my/functional-capacity" element={<LazyFunctionalCapacityPage />} />
          <Route path="/maps-overview" element={<LazyMapsOverviewPage />} />
          <Route path="/studio" element={<StudioLayout />}>
            <Route element={<StudioEmployeeGuard />}>
              <Route index element={<StudioIndexRedirect />} />
              <Route path="videos" element={<LazyVideoLibraryPage />} />
              <Route path="videos/:id" element={<LazyVideoDetailPage />} />
              <Route path="exercises" element={<LazyExerciseLibraryPage />} />
              <Route path="templates" element={<LazyTemplateLibraryPage />} />
              <Route path="templates/new" element={<LazyTemplateFormPage />} />
              <Route path="templates/:id" element={<LazyTemplateFormPage />} />
              <Route path="self-care" element={<LazySelfCareListPage />} />
              <Route path="self-care/:id" element={<LazySelfCareEditorPage />} />
              <Route path="bundles" element={<LazyBundleListPage />} />
              <Route path="bundles/new" element={<LazyBundleWizardPage />} />
              <Route path="bundles/:id" element={<LazyBundleDetailPage />} />
              <Route path="bundles/:id/edit" element={<LazyBundleWizardPage />} />
              <Route path="player/template/:templateId" element={<LazyStudioTemplatePlayerPage />} />
              <Route path="player/:bundleId" element={<LazyStudioPlayerPage />} />
            </Route>
          </Route>
          <Route
            path="/employee/:employeeId/maps"
            element={
              <EmployeeSessionGate>
                <LazyEmployeeMapsPage />
              </EmployeeSessionGate>
            }
          />
          <Route
            path="/employee/:employeeId/testing"
            element={
              <EmployeeSessionGate>
                <LazyEmployeeTestingPage />
              </EmployeeSessionGate>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AppUpdateNotice />
    </AuthProvider>
  );
}

export default App;
